import type { ISerializedComponent, ISubscribable } from '../core';
import type { AssetPool } from '../core/helpers/AssetPool';
import type { Input } from '../core/helpers/Input';
import type { SceneManager } from '../core/helpers/SceneManager';
import { Topic } from '../core/helpers/Topic';
import type { Vector2 } from '../core/helpers/Vector2';
import type { Physics } from '../core/physics/Physics';
import type { Time } from '../core/Time';
import type { IGameObjectConstructionParams } from '../core/types';
import type { GameObject } from '../game-objects/GameObject';
import type { Terrain } from '../game-objects/Terrain';
import type { Transform } from './Transform';

export type ComponentType<T extends Component = Component> = {
  readonly prototype: T;
  readonly name: string;
  readonly typeName?: string;
};

export type SerializableComponentType<T extends Component = Component> = ComponentType<T> & {
  createFromSerialized?: (gameObject: GameObject, data: Record<string, unknown>) => T;
};

export abstract class Component {
  public static readonly typeName: string = 'Component';

  public readonly gameObject: GameObject;

  private isEnabled: boolean = true;

  readonly #onDestroyed = new Topic<Component>();

  public constructor(gameObject: GameObject, enabled: boolean = true) {
    this.gameObject = gameObject;
    this.enabled = enabled;
  }

  public set enabled(enabled: boolean) {
    if (enabled === this.isEnabled) {
      return;
    }

    this.isEnabled = enabled;

    if (enabled) {
      this.onEnabled();
    } else {
      this.onDisable();
    }
  }

  public get enabled(): boolean {
    if (!this.gameObject.enabled) {
      return false;
    }

    return this.isEnabled;
  }

  public get typeName(): string {
    return Component.getTypeName(this.constructor as ComponentType<Component>);
  }

  public get tag(): string {
    return this.gameObject.tag;
  }

  public get id(): string {
    return this.gameObject.id;
  }

  public get name(): string {
    return this.gameObject.name;
  }

  public get transform(): Transform {
    return this.gameObject.transform;
  }

  public get onDestroyed(): ISubscribable<Component> {
    return this.#onDestroyed;
  }

  protected get input(): Input {
    return this.gameObject.input;
  }

  protected get time(): Time {
    return this.gameObject.time;
  }

  protected get assetPool(): AssetPool {
    return this.gameObject.assetPool;
  }

  protected get sceneManager(): SceneManager {
    return this.gameObject.sceneManager;
  }

  protected get physics(): Physics {
    return this.gameObject.physics;
  }

  protected get gameCanvas(): HTMLCanvasElement {
    return this.gameObject.gameCanvas;
  }

  protected get terrain(): Terrain {
    return this.gameObject.terrain;
  }

  public static getTypeName<T extends Component>(component: ComponentType<T>): string {
    return Object.prototype.hasOwnProperty.call(component, 'typeName') && component.typeName !== undefined
      ? component.typeName
      : component.name;
  }

  public serialize(): ISerializedComponent {
    return {
      typeName: this.typeName,
      data: {}
    };
  }

  public deserialize(data: Record<string, unknown>): void {
    Object.keys(data);
  }

  public onEnabled(): void {}

  public start(): void {}

  /**
   * This function is called once every frame. Override this function in derived components if accessing the update loop is needed.
   * Do not call super() as the original implementaiton will remove itself from the update loop for performance reasons (no point in invoking a bunch of empty update methods).
   */
  public update(): void {
    this.gameObject.removeComponentFromUpdate(this);
  }

  public onDisable(): void {}

  public onDestroy(): void {
    this.#onDestroyed.publish(this);
  }

  //These are simply short cut methods to access the gameObject's functionality from a component quicker.
  public hasComponent<T extends Component>(component: ComponentType<T>): boolean {
    return this.gameObject.hasComponent(component);
  }

  public getComponent<T extends Component>(component: ComponentType<T>): T | null {
    return this.gameObject.getComponent(component);
  }

  public getComponents<T extends Component>(component: ComponentType<T>): T[] {
    return this.gameObject.getComponents(component);
  }

  public getComponentInParent<T extends Component>(component: ComponentType<T>): T | null {
    return this.gameObject.getComponentInParent(component);
  }

  public getComponentsInParent<T extends Component>(component: ComponentType<T>): T[] {
    return this.gameObject.getComponentsInParent(component);
  }

  public getComponentInChildren<T extends Component>(component: ComponentType<T>): T | null {
    return this.gameObject.getComponentInChildren(component);
  }

  public getComponentsInChildren<T extends Component>(component: ComponentType<T>): T[] {
    return this.gameObject.getComponentsInChildren(component);
  }

  public addComponent<T extends Component>(newComponent: T): T {
    return this.gameObject.addComponent(newComponent);
  }

  public removeComponent(component: Component): void {
    this.gameObject.removeComponent(component);
  }

  protected findGameObjectById(id: string): GameObject | null {
    return this.gameObject.findGameObjectById(id);
  }

  protected findGameObjectWithTag(tag: string): GameObject | null {
    return this.gameObject.findGameObjectWithTag(tag);
  }

  protected findGameObjectsWithTag(tag: string): GameObject[] {
    return this.gameObject.findGameObjectsWithTag(tag);
  }

  protected instantiate<T extends GameObject>(
    type: new (constructionParams: IGameObjectConstructionParams) => T,
    position?: Vector2,
    rotation?: number,
    parent?: Transform
  ): GameObject {
    return this.gameObject.instantiate(type, position, rotation, parent);
  }

  protected destroy(object: GameObject, time: number = 0): void {
    this.gameObject.destroy(object, time);
  }

  protected invoke(funcToInvoke: () => void, time: number): void {
    this.gameObject.invoke(funcToInvoke, time);
  }

  protected invokeRepeating(funcToInvoke: () => void, repeatRate: number, cancelToken?: { cancel: boolean }): void {
    this.gameObject.invokeRepeating(funcToInvoke, repeatRate, cancelToken);
  }
}
